import * as React from 'react';
import ConfirmDialog from 'components/ConfirmDialog';
import { confirmDialogState, claimConfirmDialogRendering } from 'state/confirm-dialog';

/**
 * Global ConfirmDialog component that renders based on the confirm-dialog state.
 * This should be rendered once at the app root level.
 *
 * @example
 * ```tsx
 * import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourAppContent />
 *       <GlobalConfirmDialog />
 *     </>
 *   );
 * }
 * ```
 */
export default function GlobalConfirmDialog() {
  const dialogState = confirmDialogState.useState();

  // Only render if this instance successfully claims rendering rights
  const canRender = React.useMemo(() => {
    if (dialogState.isOpen && !dialogState.isBeingRendered) {
      return claimConfirmDialogRendering();
    }
    return dialogState.isBeingRendered;
  }, [dialogState.isOpen, dialogState.isBeingRendered]);

  if (!canRender || !dialogState.isOpen) {
    return null;
  }

  return (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
      data-testid={dialogState['data-testid']}
    >
      {dialogState.children}
    </ConfirmDialog>
  );
}
